import { useCallback, useRef } from 'react';
import { useMenuRouter, useRouter } from 'scbk-services';
import {
  Dialog,
  useKCddCompleteViewModel,
  usePageMove,
  usePhoneAuthContext,
  useUsrHelpLoginAuthSate,
} from 'scbk-shared-module';
import { useGetNonFaceAuthInfo, useSvcMemGetCustInfo } from './api/remoteIdvCheckHooks';

import { useTranslation } from 'react-i18next';
import type {
  AcctVerificationRouteData,
  GetNonFaceAuthInfoRequest,
  GetNonFaceAuthInfoResponse,
  IdVerificationRouteData,
  RemoteIdvCheckInfo,
  RemoteIdvCheckParam,
  UsrHelpLoginAuthSateResponse,
} from 'scbk-shared-module';

export const useRemoteIdvCheck = () => {
  const { request: custInfoRequest } = useSvcMemGetCustInfo();
  const { request: getNonFaceAuthInfoRequeset } = useGetNonFaceAuthInfo();
  const { request: getLoginAuthState } = useUsrHelpLoginAuthSate();
  const { request: getKCddCompleteViewModel } = useKCddCompleteViewModel(); // K-CDD KAIS 통신 화면 모델 조회 [AS-IS MA3CMMBIZ028_201S]
  const { goMenu } = useMenuRouter();
  const { openAuthPop } = usePhoneAuthContext();
  const { goMain } = usePageMove();

  const { t } = useTranslation();

  const router = useRouter();
  const authReqRef = useRef<RemoteIdvCheckParam>({ bizType: '' });

  /**
   * 고객정보조회
   */
  const getCustInfo = useCallback(
    async (request: RemoteIdvCheckParam) => {
      const res = (await custInfoRequest(request)) as RemoteIdvCheckInfo;
      const bizType = request.bizType || '';

      if (bizType !== '') {
        // 최소나이, 이용가능나이 체크
        if (res.isLowAge === 'Y' && res.checkAge !== 'Y') {
          //MA3AAT001046
          await Dialog.alert({
            content: t('RemoteIdv.message001', {
              defaultValue: '미성년자(만19세 미만)는 서비스를 이용할 수 없어요.',
            }),
            okBtnLabel: t('RemoteIdv.okBtnLabel', { defaultValue: '확인' }),
          });

          //TODO 이동화면 확인 필요
          goMain({ type: 'PRODUCT' });

          return { successYn: 'N' };
        }

        // 외국인 체크
        if (res.isForeigner === 'Y') {
          if ((res.isAllowed || 'N') === 'N') {
            //res.errCode || MA3AAT001074
            await Dialog.alert({
              content: [
                t('RemoteIdv.message002', { defaultValue: '외국인은 이용할 수 없어요.' }),
                t('RemoteIdv.message003', { defaultValue: '가까운 영업점을 방문해 주세요.' }),
              ],
              okBtnLabel: t('RemoteIdv.okBtnLabel', { defaultValue: '확인' }),
            });

            //TODO 이동화면 확인 필요
            goMain({ type: 'PRODUCT' });

            return { successYn: 'N' };
          }
        }
      }

      const kcddParam: Record<string, unknown> = {
        initFlag: 'Y',
        scrnDataInfo: JSON.stringify(request),
      };

      // 위험등급 체크
      if (res.yoCddKycLv === (request.cddCheckOption?.cddKycLv || 'E')) {
        const kCddCompleteViewModelRouteData = await getKCddCompleteViewModel({ ...kcddParam });

        await Dialog.alert({
          title: t('RemoteIdv.title001', { defaultValue: '[고객확인제도 안내]' }),
          content: [
            t('RemoteIdv.message004', {
              defaultValue:
                '[특정 금융거래정보의 보고 및 이용 등에 관한 법률」제5조의2(금융회사 등의 고객 확인의무)]에 따라 고객확인업무가 필요해요.',
            }),
            t('RemoteIdv.message005', {
              defaultValue:
                '모바일 뱅킹 [전체메뉴 > 고객센터 > 고객확인제도] 에서 고객정보를 확인하고 최신 내용으로 변경 후 서비스를 이용해 주세요',
            }),
            ' ',
            t('RemoteIdv.message006', {
              defaultValue:
                '※ 고객정보가 확인되지 않을 경우 은행 일부거래가 제한될 수 있어요. 고객정보 변경 후, 다시 이용해 주세요.',
            }),
          ],
          okBtnLabel: t('RemoteIdv.okBtnLabel', { defaultValue: '확인' }),
        });

        router.push('COMCDD001_84MS', {
          routeData: { ...kCddCompleteViewModelRouteData },
          // historyOptions: { clearHistory: true },
        });

        return { successYn: 'N' };
      }

      // CDD 접수 중 체크
      if (res.yoCddPgj === (request.cddCheckOption?.cddPgj || 'Y')) {
        const kCddCompleteViewModelRouteData = await getKCddCompleteViewModel({ ...kcddParam });

        const isOk = await Dialog.confirm({
          content: [
            t('RemoteIdv.message007', {
              defaultValue: '고객정보를 확인하고 있어요.',
            }),
            t('RemoteIdv.message008', {
              defaultValue: '확인이 완료돼야 서비스 이용이 가능해요.',
            }),
            t('RemoteIdv.message009', {
              defaultValue: '진행상황을 확인할까요?',
            }),
          ],
          okBtnLabel: t('RemoteIdv.okBtnLabel', { defaultValue: '확인' }),
          cancelBtnLabel: t('RemoteIdv.cancelBtnLabel', { defaultValue: '취소' }),
        });

        if (isOk) {
          // K-CDD 접수중 페이지(COMCDD01_81MS) 작성
          router.push('COMCDD001_81MS', {
            routeData: { ...kCddCompleteViewModelRouteData },
            // historyOptions: { clearHistory: true },
          });
        } else {
          // 홈으로 이동
          goMain({ type: 'HOME' });
        }

        return { successYn: 'N' };
      }

      if (!bizType.match(/^MCDD|MHLR|MPLR|MOLR|MBLR|MRLR|CUSA|WCDL$/)) {
        if (res?.yoHumGb === 'Y') {
          //휴면계좌보유
          await Dialog.alert({
            content: [
              t('RemoteIdv.message010', {
                defaultValue:
                  '휴면계좌를 보유하고 있어요. 영업점 방문 또는 모바일뱅킹으로 해제할 수 있어요.',
              }),
              t('RemoteIdv.message011', {
                defaultValue: '[인증/지원센터>휴면계좌 활성화]',
              }),
            ],
            okBtnLabel: t('RemoteIdv.okBtnLabel', { defaultValue: '확인' }),
          });

          goMain({ type: 'HOME' });

          return { successYn: 'N' };
        }
      }

      if (
        request.checkDepositWithdrawAcct === 'Y' &&
        (!res.yoMyInfRec || res.yoMyInfRec.length === 0)
      ) {
        // 입출금계좌 미보유
        const isOK = await Dialog.confirm({
          content: [
            t('RemoteIdv.message012', {
              defaultValue: 'SC제일은행 입출금계좌 보유자만 신청 가능해요.',
            }),
            t('RemoteIdv.message013', {
              defaultValue: '입출금 목록화면으로 이동하시겠어요?',
            }),
          ],
          okBtnLabel: t('RemoteIdv.moveBtnLabel', { defaultValue: '이동' }),
          cancelBtnLabel: t('RemoteIdv.cancelBtnLabel', { defaultValue: '취소' }),
        });

        if (!isOK) {
          // TODO 화면이동 확인 취소 버튼 클릭 처리
          goMain({ type: 'PRODUCT' });
          return { successYn: 'N' };
        }

        // TODO 화면이동 확인 확인 버튼 클릭 처리
        goMenu('MA3PRDACT001');
        return { successYn: 'N' };
      }

      return { ...res, successYn: 'Y' };
    },
    [custInfoRequest],
  );

  /**
   * 신분증 촬영 화면 이동
   */
  const goIdVerification = useCallback((routeData: IdVerificationRouteData) => {
    router.push('COMEKC002_10MS', { routeData: routeData });
  }, []);

  /**
   * 계좌인증 화면 이동
   */
  const goAcctVerification = useCallback((routeData: AcctVerificationRouteData) => {
    router.push('COMEKC005_10MS', { routeData: routeData });
  }, []);

  /**
   * 비대면 인증 정보 조회
   */
  const getNonFaceAuthInfo = useCallback(
    async (request: GetNonFaceAuthInfoRequest) => {
      const res = (await getNonFaceAuthInfoRequeset(request)) as GetNonFaceAuthInfoResponse;

      return res;
    },
    [getNonFaceAuthInfoRequeset],
  );

  /**
   * 로그인 or 인증 여부 체크 후 고객정보 조회 or 본인인증 후 고객정보 조회
   */
  const getCustInfoWithLoginOrAuth = useCallback(
    async (request: RemoteIdvCheckParam) => {
      authReqRef.current = request;
      const result = (await getLoginAuthState({
        checkType: request.checkType || '',
      })) as UsrHelpLoginAuthSateResponse;

      if (result.loginOrAuthYn === 'Y') {
        return await getCustInfo(request);
      } else {
        return await openPopup(authReqRef.current);
      }
    },
    [getLoginAuthState],
  );

  /**
   * 본인인증 팝업 호출
   */
  const openPopup = useCallback(
    async (request: RemoteIdvCheckParam) => {
      const res = (await openAuthPop(request)) as RemoteIdvCheckInfo | undefined;

      if (!res || JSON.stringify(res) === '{}') {
        return { successYn: 'N' };
      } else {
        console.log('인증 완료 후 응답 => ', JSON.stringify(res));

        return { ...res };
      }
    },
    [openAuthPop],
  );

  return {
    getCustInfo,
    remoteIdvCheck: getCustInfo,
    checkProceedability: getCustInfoWithLoginOrAuth,
    goIdVerification,
    goAcctVerification,
    getNonFaceAuthInfo,
    openViewAuthPopup: openPopup,
  };
};
